import { utc, UTCDateMini } from "@date-fns/utc"
import {
  addDays,
  addMilliseconds,
  addMonths,
  addWeeks,
  differenceInCalendarDays,
  differenceInCalendarMonths,
  differenceInDays,
  differenceInWeeks,
  eachDayOfInterval,
  eachMonthOfInterval,
  eachWeekOfInterval,
  endOfDay,
  endOfISOWeek,
  endOfMonth,
  endOfQuarter,
  endOfYear,
  format,
  formatDistanceToNow,
  formatRelative,
  getDaysInMonth,
  getISODay,
  getISOWeek,
  getMonth,
  getYear,
  isMonday as isMonday_,
  isSameDay as isSameDay_,
  isSameISOWeek,
  isSameMonth as isSameMonth_,
  isSunday as isSunday_,
  isValid,
  setISOWeek,
  startOfDay,
  startOfISOWeek,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  subMilliseconds,
  max as maxDate,
  subWeeks,
} from "date-fns"
import { enGB } from "date-fns/locale"

const MILLISECONDS_IN_WEEK = 7 * 24 * 60 * 60 * 1000
export const months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
]

export type MonthCategoryPoint = { category: string; value: number }
export type MonthTimePoint = { milliseconds: number; value: number }
type MonthInputPoint = { date: Date; value: number }

export type GroupByMonthMode = "category" | "time"

export type DateAlignmentGranularity = "month" | "week"

export function startOfDayUTC(date: Date | string | number) {
  if (typeof date === "string") {
    date = new UTCDateMini(date)
  } else if (typeof date === "number") {
    date = new UTCDateMini(date)
  }

  return startOfDay(date, { in: utc })
}

export function endOfDayUTC(date: Date) {
  return endOfDay(date, { in: utc })
}

export function currentDateTimeUTC() {
  return new UTCDateMini(Date.now())
}
export function startOfTodayUTC() {
  return startOfDayUTC(Date.now())
}

/**
 * The stock projection only walks forward from today's snapshot, so a date that has already passed
 * has no projection to give — both the current engine and the one before it returned a constant
 * that described nothing. Callers holding a date that goes stale on its own (a PO's expected
 * arrival, a collection's start) read it as today rather than asking for that number.
 */
export function notBeforeTodayUTC(date: Date, today = startOfTodayUTC()) {
  return date < today ? today : date
}

export function getStartOfCurrentWeekUTC() {
  return startOfISOWeek(new UTCDateMini(), { in: utc })
}

export function getEffectiveForecastStartDate(startDate: Date) {
  const currentWeekStart = getStartOfCurrentWeekUTC()
  return startDate.getTime() > currentWeekStart.getTime() ? startDate : currentWeekStart
}

export function getEndOfCurrentWeekUTC() {
  return endOfISOWeek(new UTCDateMini(), { in: utc })
}

/** UTC `YYYY-MM-DD` key for SQL `::date` bounds. `toISOString()` is always UTC (the `Z`),
 *  unlike date-fns `format`, which renders in local time. */
export function toUTCDateString(date: Date): string {
  return date.toISOString().slice(0, 10)
}

/** Returns the UTC Monday timestamp (ms) for a given date — use to bucket daily data into ISO weeks. */
export function utcMondayMs(date: Date): number {
  const dayOfWeek = date.getUTCDay() // 0=Sun, 1=Mon … 6=Sat
  const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1
  const ms = date.getTime() - daysToMonday * 86_400_000
  return ms - (ms % 86_400_000)
}

export function getStartOfCurrentMonthUTC() {
  return startOfMonth(new UTCDateMini(), { in: utc })
}

export function getStartOfCurrentYearUTC() {
  return startOfYear(new UTCDateMini(), { in: utc })
}

export function getEndOfCurrentMonthUTC() {
  return endOfMonth(new UTCDateMini(), { in: utc })
}

export function convertToStartOfWeekUTC(date: Date) {
  return startOfISOWeek(date, { in: utc })
}

export function convertToEndOfWeekUTC(date: Date) {
  return endOfISOWeek(date, { in: utc })
}

export function startOfWeekOnOrAfterUTC(date: Date) {
  const weekStart = convertToStartOfWeekUTC(date)
  return weekStart < date ? convertToStartOfWeekUTC(addWeeks(date, 1)) : weekStart
}

export function endOfWeekOnOrBeforeUTC(date: Date) {
  const weekEnd = startOfDayUTC(convertToEndOfWeekUTC(date))
  return weekEnd > date ? startOfDayUTC(convertToEndOfWeekUTC(subWeeks(date, 1))) : weekEnd
}

export function convertToStartOfMonthUTC(date: Date) {
  return startOfMonth(date, { in: utc })
}

export function convertToEndOfMonthUTC(date: Date) {
  return endOfMonth(date, { in: utc })
}

export function convertToStartOfYearUTC(date: Date) {
  return startOfYear(date, { in: utc })
}

export function convertToEndOfYearUTC(date: Date) {
  return endOfYear(date, { in: utc })
}

export function getStartOfYearUTC(year: number) {
  return startOfYear(new UTCDateMini(year, 0, 1), { in: utc })
}

export function convertToStartOfQuarterUTC(date: Date) {
  return startOfQuarter(date, { in: utc })
}

export function convertToEndOfQuarterUTC(date: Date) {
  return endOfQuarter(date, { in: utc })
}

export function differenceInCalendarDaysUTC(dateLeft: Date, dateRight: Date) {
  return differenceInCalendarDays(dateLeft, dateRight, { in: utc })
}

export function differenceInWeeksUTC(dateLeft: Date, dateRight: Date) {
  return differenceInWeeks(dateLeft, dateRight, { in: utc })
}

// Whole ISO weeks spanned by [start, end] inclusive (snaps to Mon→Sun, so +1 to count both ends).
export function weeksInRange(start: Date, end: Date) {
  const weeks = differenceInWeeksUTC(setISOWeekDay(end, 7), setISOWeekDay(start, 1)) + 1
  return Math.max(weeks, 1)
}

export function addMonthsUTC(date: Date, amount: number) {
  return addMonths(date, amount, { in: utc })
}

export function getMonthUTC(date: Date) {
  return getMonth(date, { in: utc })
}

export function differenceInCalendarMonthsUTC(dateLeft: Date, dateRight: Date) {
  return differenceInCalendarMonths(dateLeft, dateRight, { in: utc })
}

export function getYearUTC(date: Date) {
  return getYear(date, { in: utc })
}

export function isSameDay(date1: Date, date2: Date) {
  return isSameDay_(date1, date2, { in: utc })
}

export function isSameWeek(date1: Date, date2: Date) {
  return isSameISOWeek(date1, date2, { in: utc })
}

export function isSameMonth(date1: Date, date2: Date) {
  return isSameMonth_(date1, date2, { in: utc })
}

export function isStartOfMonth(date: Date) {
  return date.getUTCDate() === 1
}

export function isEndOfMonth(date: Date) {
  return date.getUTCDate() === getDaysInMonth(date, { in: utc })
}

export function isToday(date: Date) {
  return isSameDay(date, startOfTodayUTC())
}

export function isMonday(date: Date) {
  return isMonday_(date, { in: utc })
}

export function isSunday(date: Date) {
  return isSunday_(date, { in: utc })
}

// Special functions for <DatePicker /> component, because internally it uses local timezone dates.
export function isMondayDatePicker(date: Date) {
  return getISODay(date) === 1
}

export function isFuture(date: Date) {
  return date.getTime() > startOfTodayUTC().getTime()
}

export function isPast(date: Date) {
  return date.getTime() < startOfTodayUTC().getTime()
}

export function isBetween(date: Date, startDate: Date, endDate: Date): boolean {
  return date.getTime() >= startDate.getTime() && date.getTime() <= endDate.getTime()
}

export function isBefore(date: Date, dateToCompare: Date) {
  return date.getTime() < dateToCompare.getTime()
}

export function isAfter(date: Date, dateToCompare: Date) {
  return date.getTime() > dateToCompare.getTime()
}

export function isEqual(date: Date, dateToCompare: Date) {
  return date.getTime() === dateToCompare.getTime()
}

export function isThisWeek(date: Date) {
  return isSameWeek(new UTCDateMini(), date)
}

export function isThisMonth(date: Date) {
  return isSameMonth(new UTCDateMini(), date)
}

export const setISOWeekDay = (day: Date, weekday: number) => {
  // iso weekday, where 1 is Monday and 7 is Sunday
  const currentIsoWeekDay = getISODay(day)
  return addDays(day, weekday - currentIsoWeekDay)
}

export enum DateFormatType {
  Default = "default",
  DefaultYearFirst = "defaultYearFirst",
  DefaultYearFirstWithTime = "defaultYearFirstWithTime",
  WithTime = "withTime",
  FullDate = "fullDate",
  FullDateShort = "fullDateShort",
  LongDate = "longDate",
  DayMonth = "dayMonth",
  Week = "week",
  WeekShort = "weekShort",
  WeekNumber = "weekNumber",
  WeekYear = "weekYear",
  Month = "month",
  MonthDay = "monthDay",
  MonthDaySecondary = "monthDaySecondary",
  MonthDayYear = "monthDayYear",
  MonthDayFull = "monthDayFull",
  MonthYear = "monthYear",
  MonthYearFull = "monthYearFull",
  YearMonthKey = "yearMonthKey",
  WeekMonthYear = "weekMonthYear",
  QuarterYear = "quarterYear",
  GraphTime = "graphTime",
  GraphDay = "graphDay",
  GraphWeek = "graphWeek",
  GraphMonth = "graphMonth",
  GraphMonthWithoutYear = "graphMonthWithoutYear",
  GraphQuarter = "graphQuarter",
  PointInTime = "pointInTime",
  TimeOnly = "timeOnly",
  /** Wall-clock stamp: "8 Sep 2026, 20:01". */
  DayMonthYearWithTime = "dayMonthYearWithTime",
  /** Wall-clock day heading: "Tue, 8 Sep". */
  WeekdayDayMonth = "weekdayDayMonth",
}

/**
 * Formats read in the viewer's own clock rather than the UTC business date. Everything else is a
 * business date and is normalised to UTC below, which would file a late-evening local timestamp
 * under the following day.
 */
const LOCAL_TIME_FORMATS = new Set<DateFormatType>([
  DateFormatType.WithTime,
  DateFormatType.DefaultYearFirstWithTime,
  DateFormatType.GraphTime,
  DateFormatType.DayMonthYearWithTime,
  DateFormatType.WeekdayDayMonth,
  DateFormatType.TimeOnly,
])

export const formatDate = (
  dateInput: Date | string | number | undefined | null,
  formatType: DateFormatType = DateFormatType.Default,
): string => {
  const formatTypeString = formatType as DateFormatType
  if (!dateInput || !formatTypeString) {
    return "Undefined"
  }

  let dt = new Date(dateInput)

  if (!isValid(dt)) {
    return "-"
  }

  if (!LOCAL_TIME_FORMATS.has(formatTypeString)) {
    dt = new UTCDateMini(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate())
  }

  switch (formatTypeString) {
    case DateFormatType.Default:
      return format(dt, getDateFormat())
    case DateFormatType.DefaultYearFirst:
      return format(dt, "yyyy-MM-dd")
    case DateFormatType.DefaultYearFirstWithTime:
      return format(dt, "yyyy-MM-dd HH:mm")
    case DateFormatType.WithTime:
      return format(dt, getDateAndTimeFormat())
    case DateFormatType.FullDate:
      return format(dt, "d MMMM yyyy")
    case DateFormatType.FullDateShort:
      return format(dt, "d MMM yyyy")
    case DateFormatType.DayMonthYearWithTime:
      return format(dt, "d MMM yyyy, HH:mm")
    case DateFormatType.WeekdayDayMonth:
      return format(dt, "EEE, d MMM")
    case DateFormatType.DayMonth:
      return format(dt, "dd MMM")
    case DateFormatType.LongDate:
      return format(dt, "PPP")
    case DateFormatType.Week:
      return format(dt, "'Week' I") // ISO Week
    case DateFormatType.WeekShort:
      return format(dt, "'W' I") // ISO Week
    case DateFormatType.WeekNumber:
      return format(dt, "I") // ISO Week
    case DateFormatType.WeekYear:
      return format(dt, "I R") // ISO Week
    case DateFormatType.Month:
      return format(dt, "MMM")
    case DateFormatType.MonthDay:
      return format(dt, "MM-dd")
    case DateFormatType.MonthDaySecondary:
      return format(dt, "MMM dd")
    case DateFormatType.MonthDayYear:
      return format(dt, "MMM dd, yyyy")
    case DateFormatType.MonthDayFull:
      return format(dt, "MMMM-dd")
    case DateFormatType.MonthYear:
      return format(dt, "MMM yyyy")
    case DateFormatType.MonthYearFull:
      return format(dt, "MMMM yyyy")
    case DateFormatType.WeekMonthYear:
      return format(dt, "'Week' I - MMMM yyyy") // ISO Week
    case DateFormatType.YearMonthKey:
      return format(dt, "yyyy-MM")
    case DateFormatType.QuarterYear:
      return format(dt, "'Q'Q - yyyy")
    case DateFormatType.GraphTime:
      return format(dt, getDateAndTimeFormat())
    case DateFormatType.GraphDay:
      return format(dt, "EEE, dd MMM, yyyy")
    case DateFormatType.GraphWeek:
      // Tooltips pair this with the week's date range, so no "from <date>" here. RRRR is the
      // ISO week-numbering year (a 29 Dec can belong to week 1 of the next year).
      return format(dt, "'Week' I, RRRR")
    case DateFormatType.GraphMonth:
      return format(dt, "MMMM, yyyy")
    case DateFormatType.GraphMonthWithoutYear:
      return format(dt, "MMM")
    case DateFormatType.GraphQuarter:
      return format(dt, "'Q'Q 'from' MMM yyyy")
    case DateFormatType.PointInTime:
      return format(dateInput, "d MMMM yyyy 'at' HH:mm")
    case DateFormatType.TimeOnly:
      return format(dt, "HH:mm")
  }
}

interface FormatDateRangeParams {
  startDate: Date | string | number | undefined | null
  endDate: Date | string | number | undefined | null
  dateFormatType?: DateFormatType
  /** `"to"` for running copy ("1 Jun 2025 to 31 Jan 2026"); the dash suits compact labels. */
  separator?: "-" | "to"
}

export const formatDateRange = ({
  startDate,
  endDate,
  dateFormatType = DateFormatType.FullDate,
  separator = "-",
}: FormatDateRangeParams): string => {
  return `${formatDate(startDate, dateFormatType)} ${separator} ${formatDate(endDate, dateFormatType)}`
}

/** Earliest to latest of `dates`; a single date when they share a day, "-" when there are none. */
export const formatDateSpan = (
  dates: (Date | null)[],
  dateFormatType: DateFormatType = DateFormatType.FullDateShort,
): string => {
  const sorted = dates
    .filter((date): date is Date => date !== null)
    .toSorted((a, b) => a.getTime() - b.getTime())
  const earliest = sorted[0]
  const latest = sorted.at(-1)
  if (!earliest || !latest) return "-"
  if (isSameDay(earliest, latest)) return formatDate(earliest, dateFormatType)
  return formatDateRange({ startDate: earliest, endDate: latest, dateFormatType })
}

/**
 * Formats "YYYY-MM" month keys as a compact range: "Mar 2026",
 * "Mar - May 2026", or "Nov 2026 - Feb 2027". Months in between are implied,
 * not checked — pass `formatMonthKeys` output alongside when gaps matter.
 */
export const formatMonthKeyRange = (months: string[]): string => {
  const sorted = [...months].sort()
  const first = sorted[0]
  const last = sorted[sorted.length - 1]
  if (!first || !last) return ""
  if (first === last) return formatDate(`${first}-01`, DateFormatType.MonthYear)
  const firstLabel =
    first.slice(0, 4) === last.slice(0, 4)
      ? formatDate(`${first}-01`, DateFormatType.Month)
      : formatDate(`${first}-01`, DateFormatType.MonthYear)
  return `${firstLabel} - ${formatDate(`${last}-01`, DateFormatType.MonthYear)}`
}

/** Formats "YYYY-MM" month keys as an explicit sorted list: "Mar 2026, Jun 2026". */
export const formatMonthKeys = (months: string[]): string =>
  [...months]
    .sort()
    .map((m) => formatDate(`${m}-01`, DateFormatType.MonthYear))
    .join(", ")

const formatRelativeLocaleWithTime = {
  lastWeek: "'last' eeee 'at' p",
  yesterday: "'yesterday at' p",
  today: "'today at' p",
  tomorrow: "'tomorrow at' p",
  nextWeek: "eeee 'at' p",
  other: "P",
}

const formatRelativeLocale = {
  lastWeek: "'last' eeee",
  yesterday: "'yesterday",
  today: "'today'",
  tomorrow: "'tomorrow'",
  nextWeek: "eeee",
  other: "P",
}

const locale = (withTime: boolean = false) => ({
  ...enGB,
  formatRelative: (token: any) =>
    // @ts-ignore
    withTime ? formatRelativeLocaleWithTime[token] : formatRelativeLocale[token],
})

/**
 * Formats using the relative format, but if it is more than 6 days ago, it will show the full date. Include the formatType if you want to include the time.
 *
 * @param dateInput - The date to format. Can be a Date object, a string, or undefined.
 * @param baseDate - The base date to compare against. Defaults to the current date.
 * @param formatType - The format type to use. Defaults to DateFormatType.Default. IMPORTANT: If the relative date should include time, then pass the DateFormatType.WithTime.
 * @returns The formatted relative date as a string.
 */
export const formatRelativeDate = (
  dateInput: Date | string | undefined,
  baseDate: Date = new UTCDateMini(),
  formatType: DateFormatType = DateFormatType.Default,
): string => {
  if (!dateInput) {
    return "Undefined"
  }

  let dt = new Date(dateInput)

  if (!isValid(dt)) {
    return "Invalid date"
  }

  if (formatType !== DateFormatType.WithTime) {
    dt = new UTCDateMini(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate())
  }

  if (differenceInDays(baseDate, dt) > 6) {
    // When more than 6 days ago it shows a full date, so we want to control the format.
    return formatDate(dt, formatType)
  } else {
    return formatRelative(dt, baseDate, {
      locale: locale(formatType === DateFormatType.WithTime),
    })
  }
}

/**
 * Compact "time ago" phrasing (e.g. "21 minutes ago"). Use for a running relative distance;
 * `formatRelativeDate` for calendar-style wording ("today at …"), `formatDate` for absolute.
 */
export const formatTimeAgo = (dateInput: Date | string | number | undefined | null): string => {
  if (dateInput == null) return "Undefined"
  const dt = new Date(dateInput)
  if (!isValid(dt)) return "-"
  return formatDistanceToNow(dt, { addSuffix: true })
}

export function differenceInWeeksFloat(day1: Date, day2: Date) {
  return (day2.getTime() - day1.getTime()) / MILLISECONDS_IN_WEEK
}

export function getStartOfWeekDatesBetween(startDate: Date, endDate: Date) {
  return eachWeekOfInterval({ start: startDate, end: endDate }, { weekStartsOn: 1, in: utc })
}

export function getDaysBetween(startDate: Date, endDate: Date) {
  return eachDayOfInterval({ start: startDate, end: endDate }, { in: utc })
}

export function getStartOfFullMonthDatesBetween(startDate: Date, endDate: Date) {
  if (isAfter(startDate, endDate)) {
    return []
  }

  const months = eachMonthOfInterval(
    {
      start: startDate,
      end: endDate,
    },
    { in: utc },
  )

  if (!isStartOfMonth(startDate)) {
    // If the start date is not the start of the month, remove the first month
    months.shift()
  }

  if (!isEndOfMonth(endDate)) {
    // If the end date is not the end of the month, remove the last month
    months.pop()
  }

  return months
}

/**
 * Period starts to request projected closing stock for: the current period (week or
 * month) through the end of the window. The monthly grid is anchored to the start of
 * the month containing `startDate`, because `getStartOfFullMonthDatesBetween` drops a
 * partial first month and would otherwise leave the current month without a value
 * whenever the window starts mid-month.
 */
export function getProjectionPeriodStarts(startDate: Date, endDate: Date, monthly: boolean) {
  const currentPeriodStart = monthly ? getStartOfCurrentMonthUTC() : getStartOfCurrentWeekUTC()
  const grid = monthly
    ? getStartOfFullMonthDatesBetween(convertToStartOfMonthUTC(startDate), addWeeks(endDate, 1))
    : getStartOfWeekDatesBetween(startDate, endDate)
  return grid.filter((date) => date >= currentPeriodStart)
}

export function getMonthDatesBetween(startDate: Date, endDate: Date) {
  if (isAfter(startDate, endDate)) {
    return []
  }
  return eachMonthOfInterval({ start: startDate, end: endDate }, { in: utc })
}

export function findWeekCutOffIndex(weeks: Date[], cutOfDate: Date): number {
  return weeks.findIndex((week) => {
    const weekStart = convertToStartOfWeekUTC(week)
    const weekEnd = convertToEndOfWeekUTC(week)
    return isBetween(cutOfDate, weekStart, weekEnd)
  })
}

export function findMonthCutOffIndex(months: Date[], cutOffDate: Date): number {
  return months.findIndex((month) => {
    const monthStart = convertToStartOfMonthUTC(month)
    const monthEnd = convertToEndOfMonthUTC(month)
    return isBetween(cutOffDate, monthStart, monthEnd)
  })
}

export function isLessThanXDays(date: Date | undefined, days: number): boolean {
  let dateToCheck: Date
  if (!date) return false
  if (date instanceof Date) dateToCheck = date
  else dateToCheck = date

  const now = new UTCDateMini()
  const diffInDays = Math.abs(differenceInCalendarDays(dateToCheck, now))
  return diffInDays < days
}

/*
  Takes 2 dates and returns a normalized range from Monday to Sunday.
  Returns both as null if one of them is null.
*/
export function normalizeDateRange(
  rawStartDate: Date | undefined,
  rawEndDate: Date | undefined,
): { startDate: Date | undefined; endDate: Date | undefined } {
  if (!rawStartDate || !rawEndDate) return { startDate: undefined, endDate: undefined }

  // If same week, we just want the range to be the week
  if (isSameISOWeek(rawStartDate, rawEndDate)) {
    return {
      startDate: convertToStartOfWeekUTC(rawStartDate),
      endDate: convertToEndOfWeekUTC(rawEndDate),
    }
  }

  // If start is between monday and thursday, we want to start the week on monday else we take start of next week
  const startDate =
    getISODay(rawStartDate) <= 4
      ? convertToStartOfWeekUTC(rawStartDate)
      : convertToStartOfWeekUTC(addWeeks(rawStartDate, 1))

  // If end is between Monday and Wednesday, we want the previous week's Sunday; otherwise, we take the end of the given week
  const endDate =
    getISODay(rawEndDate) <= 3
      ? convertToEndOfWeekUTC(subWeeks(rawEndDate, 1))
      : convertToEndOfWeekUTC(rawEndDate)

  return {
    startDate: startDate,
    endDate: endDate,
  }
}

export function getDateFormat() {
  // In the future we could be
  // return localStorage.getItem("timeformat") || "dd-MM-yyyy";
  // Which would allow the user to change the date format if they do a refresh
  // And localstorage is quite fast, so it should not be a problem
  return "dd-MM-yyyy"
}

export function getDateAndTimeFormat() {
  // In the future we could be
  // return localStorage.getItem("timeformat") || "dd-MM-yyyy";
  // Which would allow the user to change the date format if they do a refresh
  // And localstorage is quite fast, so it should not be a problem
  return "dd-MM-yyyy HH:mm"
}

export function convertFromStartDatesToEndDays(dates: Date[]) {
  if (dates.length < 2) {
    return dates
  }
  const time_diff =
    ((dates[dates.length - 1]?.getTime() ?? 0) - (dates[0]?.getTime() ?? 0)) / (dates.length - 1)
  return [
    ...dates.slice(1, dates.length).map((d) => subMilliseconds(d, 1)),
    addMilliseconds(dates[dates.length - 1] ?? new Date(), time_diff - 1),
  ]
}

function groupByMonthCategory(data: { date: Date; value: number }[]): MonthCategoryPoint[] {
  const grouped: Record<number, number> = {}
  data.forEach(({ date, value }) => {
    const month = date.getUTCMonth() + 1 // 1-based month
    grouped[month] = (grouped[month] ?? 0) + value
  })
  return months.map((month, index) => {
    return {
      category: month,
      value: grouped[index + 1] ?? 0,
    }
  })
}

function groupByMonthTime(year: number, data: MonthInputPoint[]): MonthTimePoint[] {
  // const firstYear = data[0]?.date.getUTCFullYear() ?? 2000
  const grouped: Record<number, number> = {}

  data.forEach(({ date, value }) => {
    const monthIdx = date.getUTCMonth()
    grouped[monthIdx] = (grouped[monthIdx] ?? 0) + value
  })

  const points: MonthTimePoint[] = []

  // Always output Jan–Dec in order
  for (let monthIdx = 0; monthIdx < 12; monthIdx++) {
    const value = grouped[monthIdx] ?? 0
    const milliseconds = Date.UTC(year, monthIdx, 1)
    points.push({ milliseconds, value })
  }

  return points
}

/**
 * Groups data points by month, summing values in each month.
 * - In "time" mode, returns 12 points (one per month) for the first year, filling missing months with zero.
 * - In "category" mode, returns 12 points (one per month name) regardless of year, summing values for each month across all years.
 */
export function groupByMonth(
  data: { date: Date; value: number }[] | undefined,
  options?: { mode?: GroupByMonthMode },
): MonthCategoryPoint[] | MonthTimePoint[] {
  const year = 2000 // doesnt matter which year we use, we just need to use the same year for all data
  if (!data || data.length === 0) return []
  if (options?.mode === "time") {
    return groupByMonthTime(year, data) as MonthTimePoint[]
  }

  return groupByMonthCategory(data) as MonthCategoryPoint[]
}

export function mapToSeason<T extends { date: Date }>(
  points: T[],
  baseYear: number,
  options: { granularity?: DateAlignmentGranularity } = { granularity: "month" },
): T[] {
  if (!points || points.length === 0) return points

  const granularity = options?.granularity
  const sorted = [...points].sort((a, b) => a.date.getTime() - b.date.getTime())

  return sorted.map((p) => {
    const d = p.date
    let syntheticDate: Date

    if (granularity === "week") {
      // Use UTC context for all ISO week operations to avoid timezone shifts
      const weekNumber = getISOWeek(d, { in: utc })
      const jan4OfBaseYear = new Date(Date.UTC(baseYear, 0, 4))
      const withWeek = setISOWeek(jan4OfBaseYear, weekNumber, { in: utc })
      syntheticDate = startOfISOWeek(withWeek, { in: utc })
    } else {
      syntheticDate = new Date(Date.UTC(baseYear, d.getUTCMonth(), d.getUTCDate()))
    }

    return { ...p, date: syntheticDate }
  })
}

const _calendarToday = new Date()
/** Default lower bound for date pickers: 5 years before today. */
export const calendarDefaultMinDate = new Date(
  _calendarToday.getFullYear() - 5,
  _calendarToday.getMonth(),
  _calendarToday.getDate(),
)
/** Default upper bound for date pickers: 5 years after today. */
export const calendarDefaultMaxDate = new Date(
  _calendarToday.getFullYear() + 5,
  _calendarToday.getMonth(),
  _calendarToday.getDate(),
)

/** Returns the later of firstSaleDate and activatedFrom, or undefined if neither is set. */
export function getSoldAndActiveSince(
  firstSaleDate: Date | null | undefined,
  activatedFrom: Date | null | undefined,
): Date | undefined {
  const dates = [firstSaleDate, activatedFrom].filter((d): d is Date => d != null)
  return dates.length > 0 ? maxDate(dates) : undefined
}

export function formatCompactAge(
  date: Date | string,
  { withAgo = false }: { withAgo?: boolean } = {},
): string {
  const minutes = Math.floor((Date.now() - new Date(date).getTime()) / 60_000)
  const suffix = withAgo ? " ago" : ""
  if (minutes < 1) return withAgo ? "just now" : "now"
  if (minutes < 60) return `${minutes}m${suffix}`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h${suffix}`
  return `${Math.floor(hours / 24)}d${suffix}`
}

const DAY_MS = 86_400_000
const WEEK_MS = 7 * DAY_MS

/** Splits already-sorted, newest-first rows into Today / This week / Older buckets. */
export function groupByAge<T>(
  items: T[],
  getDate: (item: T) => Date | string,
): { label: string; items: T[] }[] {
  const now = Date.now()
  const groups: { label: string; items: T[] }[] = [
    { label: "Today", items: [] },
    { label: "This week", items: [] },
    { label: "Older", items: [] },
  ]

  for (const item of items) {
    const age = now - new Date(getDate(item)).getTime()
    if (age < DAY_MS) groups[0]!.items.push(item)
    else if (age < WEEK_MS) groups[1]!.items.push(item)
    else groups[2]!.items.push(item)
  }

  return groups.filter((g) => g.items.length > 0)
}
